import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import logoUrl from '@/assets/img/Logo Light Mode.png';
import {
  montarParagrafoAbertura,
  montarDeclaracaoResponsabilidade,
  montarDeclaracaoDevolucao,
  montarLocaleData,
  TEXTO_INTRODUCAO_CLAUSULAS,
  CLAUSULAS_RESPONSABILIDADE,
  TEXTO_ATESTADO_DEVOLUCAO,
  CLAUSULA_LGPD,
  LABEL_COLABORADOR,
  LABEL_TESTEMUNHA,
  LABEL_SEGURANCA_INFORMACAO,
  type EmpresaProprietaria,
  type TipoFluxoMovimentacao,
} from '../models';

export interface ItemEquipamentoTermo {
  equipamento: string;
  modeloMarca: string;
  condicao: string;
  patrimonio: string;
  nSerie: string;
  acessorios: string;
  /** Estado do equipamento (arranhão etc.) — coluna própria na tabela, uma por equipamento. */
  observacao: string;
}

/** Fotos de um equipamento (ou as legadas, da Movimentação) — `legenda` identifica a qual
 * equipamento as fotos pertencem no topo de cada página. */
export interface GrupoFotosTermo {
  legenda: string;
  urls: string[];
}

export interface PessoaTermo {
  nome: string;
  cpf?: string | null;
}

export interface TermoPdfDados {
  tipoFluxo: TipoFluxoMovimentacao;
  /** Cidade da linha de data do topo (ex. "Belo Horizonte, 15 de agosto de 2026") — sede
   * da Raro, não confundir com a cidade do colaborador (ver `colaborador.cidade`). */
  cidade: string;
  /** Data de emissão do documento (linha do topo) — normalmente "hoje". */
  data: Date;
  /** Raro (padrão) ou dados do Cliente — ver ConfigRegrasCliente.nomeEmpresaProprietaria. */
  empresa: EmpresaProprietaria;
  colaborador: { nome: string; funcao: string; cpf: string; cidade: string };
  /** Data de recebimento (envio) ou de devolução (devolução) — usada em "declaro ter
   * recebido/DEVOLVIDO, em {cidade}, {data}", diferente da data de emissão do topo. */
  dataDeclaracao: Date;
  /** Testemunha (Responsabilidade, com CPF) ou Técnico (Devolução, só nome) — sempre o
   * usuário SI logado que gera o termo. */
  assinanteRaro: PessoaTermo;
  equipamentos: ItemEquipamentoTermo[];
  /** Agrupadas por equipamento — cada foto vira uma página extra no fim do PDF, com a
   * legenda do seu grupo no topo. */
  gruposFotos: GrupoFotosTermo[];
}

const MARGEM = 20;
const LARGURA_TEXTO = 170;
const TAMANHO_FONTE_CORPO = 8;
/** Menor que o corpo — só as 7 cláusulas do Termo de Responsabilidade, pra caber tudo
 * (texto + assinaturas) numa única página. */
const TAMANHO_FONTE_CLAUSULAS = 7.5;
const ALTURA_LINHA = 3.8;
const ESPACO_PARAGRAFO = 3;
const ESPACO_SECAO = 5;
/** Espaço reservado no topo de cada página de foto pra legenda do equipamento. */
const ALTURA_LEGENDA_FOTO = 8;
/** Azul da marca (mesmo tom dos traços da logo) — usado só em títulos/rótulos, nunca no
 * corpo do texto jurídico (que segue preto, igual ao modelo original). */
const COR_MARCA: [number, number, number] = [3, 174, 252];
const COR_TEXTO: [number, number, number] = [20, 20, 20];

const TITULO_POR_TIPO_FLUXO: Record<TipoFluxoMovimentacao, string> = {
  envio: 'TERMO DE RESPONSABILIDADE',
  devolucao: 'TERMO DE DEVOLUÇÃO DE EQUIPAMENTO',
};

async function urlParaDataUrl(url: string): Promise<string> {
  const res = await fetch(url);
  const blob = await res.blob();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(new Error('Falha ao carregar foto anexada'));
    reader.readAsDataURL(blob);
  });
}

function obterDimensoesImagem(dataUrl: string): Promise<{ width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve({ width: img.naturalWidth, height: img.naturalHeight });
    img.onerror = () => reject(new Error('Falha ao ler dimensões da foto anexada'));
    img.src = dataUrl;
  });
}

function formatoImagem(dataUrl: string): string {
  const match = /data:image\/(\w+);/.exec(dataUrl);
  const ext = (match?.[1] ?? 'jpeg').toUpperCase();
  return ext === 'JPG' ? 'JPEG' : ext;
}

/**
 * Gera o PDF do Termo (Responsabilidade ou Devolução) já preenchido com os dados da
 * Movimentação — texto verbatim dos modelos originais (ver termoTemplates.ts), fotos
 * anexadas como páginas extras. Mesma estrutura de src/services/proposalPdfService.ts
 * (jsPDF client-side, sem Cloud Function).
 */
class TermoPdfServiceClass {
  async gerarTermoPdf(dados: TermoPdfDados): Promise<Blob> {
    const doc = new jsPDF('p', 'mm', 'a4');
    // Cabeçalho (logo) começa bem mais perto do topo da página do que o resto do
    // conteúdo (que usa MARGEM) — pedido explícito de "subir a logo o máximo possível".
    let y = 8;

    y = await this.addCabecalho(doc, dados, y);
    y = this.addLocaleData(doc, dados, y);
    y += ESPACO_SECAO - ESPACO_PARAGRAFO;

    if (dados.tipoFluxo === 'envio') {
      y = this.addParagrafo(doc, y, montarParagrafoAbertura(dados.empresa));
      y += ESPACO_SECAO - ESPACO_PARAGRAFO;
    }

    y = this.addTabelaEquipamentos(doc, dados, y);

    y = this.addParagrafo(
      doc,
      y,
      dados.tipoFluxo === 'envio'
        ? montarDeclaracaoResponsabilidade({
            nome: dados.colaborador.nome,
            funcao: dados.colaborador.funcao,
            cpf: dados.colaborador.cpf,
            data: montarLocaleData(dados.colaborador.cidade, dados.dataDeclaracao),
          })
        : montarDeclaracaoDevolucao({
            nome: dados.colaborador.nome,
            funcao: dados.colaborador.funcao,
            cpf: dados.colaborador.cpf,
            data: montarLocaleData(dados.colaborador.cidade, dados.dataDeclaracao),
          })
    );
    y += ESPACO_SECAO - ESPACO_PARAGRAFO;

    if (dados.tipoFluxo === 'envio') {
      y = this.addParagrafo(doc, y, TEXTO_INTRODUCAO_CLAUSULAS);
      y += ESPACO_SECAO - ESPACO_PARAGRAFO;
      CLAUSULAS_RESPONSABILIDADE.forEach((clausula, index) => {
        y = this.addParagrafo(doc, y, `${index + 1}. ${clausula}`, { tamanhoFonte: TAMANHO_FONTE_CLAUSULAS });
      });
    } else {
      y = this.addParagrafo(doc, y, TEXTO_ATESTADO_DEVOLUCAO);
    }
    y += ESPACO_SECAO - ESPACO_PARAGRAFO;

    y = this.addParagrafo(doc, y, CLAUSULA_LGPD);
    y += ESPACO_SECAO + 12;

    this.addAssinaturas(doc, dados, y);

    await this.anexarFotos(doc, dados.gruposFotos);

    return doc.output('blob');
  }

  private garantirEspaco(doc: jsPDF, y: number, alturaNecessaria: number): number {
    const pageHeight = doc.internal.pageSize.getHeight();
    if (y + alturaNecessaria > pageHeight - MARGEM) {
      doc.addPage();
      return MARGEM;
    }
    return y;
  }

  private addParagrafo(
    doc: jsPDF,
    y: number,
    texto: string,
    opcoes?: { negrito?: boolean; tamanhoFonte?: number }
  ): number {
    const tamanhoFonte = opcoes?.tamanhoFonte ?? TAMANHO_FONTE_CORPO;
    const alturaLinha = ALTURA_LINHA * (tamanhoFonte / TAMANHO_FONTE_CORPO);
    const espacoParagrafo = ESPACO_PARAGRAFO * (tamanhoFonte / TAMANHO_FONTE_CORPO);
    doc.setFont('helvetica', opcoes?.negrito ? 'bold' : 'normal');
    doc.setFontSize(tamanhoFonte);
    doc.setTextColor(...COR_TEXTO);
    const linhas = doc.splitTextToSize(texto, LARGURA_TEXTO);
    const altura = linhas.length * alturaLinha + espacoParagrafo;
    y = this.garantirEspaco(doc, y, altura);
    doc.text(linhas, MARGEM, y);
    return y + altura;
  }

  /** Rótulo de seção (ex. "Observações") — só negrito, preto (mesma cor do corpo do
   * texto), sem caixa alta — separado visualmente pelo peso da fonte, não pela cor. */
  private addRotuloSecao(doc: jsPDF, y: number, texto: string): number {
    y = this.garantirEspaco(doc, y, ALTURA_LINHA + ESPACO_PARAGRAFO);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(TAMANHO_FONTE_CORPO);
    doc.setTextColor(...COR_TEXTO);
    doc.text(texto, MARGEM, y);
    return y + ALTURA_LINHA + 1.5;
  }

  private async addCabecalho(doc: jsPDF, dados: TermoPdfDados, y: number): Promise<number> {
    // Logo Light Mode.png é um wordmark horizontal (1240x360, razão ~3.44:1) — a largura é
    // sempre derivada dessa razão real (nunca um valor fixo), pra não esticar/achatar a
    // imagem. Altura menor que antes (10mm) pra ficar proporcional a um título de 16pt, em
    // vez de dominar visualmente o cabeçalho.
    const alturaLogo = 10;
    const larguraLogo = alturaLogo * (1240 / 360);
    const titulo = TITULO_POR_TIPO_FLUXO[dados.tipoFluxo];

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(16);

    // Logo alinhada à mesma margem esquerda usada no resto do documento (MARGEM) — antes
    // ficava centralizada como bloco isolado, flutuando fora de alinhamento com a linha
    // azul abaixo (que já usa MARGEM..MARGEM+LARGURA_TEXTO).
    try {
      const logoDataUrl = await urlParaDataUrl(logoUrl);
      doc.addImage(logoDataUrl, 'PNG', MARGEM, y, larguraLogo, alturaLogo);
    } catch {
      // Sem logo é melhor do que quebrar a geração do termo inteiro.
    }

    // Título centralizado no intervalo entre o fim da logo e o fim da linha azul (não na
    // largura inteira da página) — mesmo intervalo horizontal que a linha ocupa.
    const inicioIntervalo = MARGEM + larguraLogo;
    const fimIntervalo = MARGEM + LARGURA_TEXTO;
    const larguraTitulo = doc.getTextWidth(titulo);
    const xTitulo = inicioIntervalo + (fimIntervalo - inicioIntervalo - larguraTitulo) / 2;

    doc.setTextColor(...COR_MARCA);
    // `baseline: 'middle'` centraliza o texto verticalmente em relação ao centro da logo.
    doc.text(titulo, xTitulo, y + alturaLogo / 2, { baseline: 'middle' });
    doc.setTextColor(...COR_TEXTO);
    y += alturaLogo + 6;

    doc.setDrawColor(...COR_MARCA);
    doc.setLineWidth(0.6);
    doc.line(MARGEM, y, MARGEM + LARGURA_TEXTO, y);
    return y + 8;
  }

  private addLocaleData(doc: jsPDF, dados: TermoPdfDados, y: number): number {
    return this.addParagrafo(doc, y, montarLocaleData(dados.cidade, dados.data));
  }

  private addTabelaEquipamentos(doc: jsPDF, dados: TermoPdfDados, y: number): number {
    autoTable(doc, {
      startY: y,
      margin: { left: MARGEM, right: MARGEM },
      styles: { fontSize: 7, textColor: COR_TEXTO },
      headStyles: { fillColor: COR_MARCA, textColor: [255, 255, 255] },
      head: [['Equipamento', 'Modelo/Marca', 'Condição', 'Patrimônio', 'Nº de Série', 'Acessórios', 'Observação']],
      body: dados.equipamentos.map((item) => [
        item.equipamento,
        item.modeloMarca,
        item.condicao,
        item.patrimonio,
        item.nSerie,
        item.acessorios,
        item.observacao,
      ]),
    });
    // jspdf-autotable anexa `lastAutoTable` ao doc em runtime — sem tipos oficiais pra isso.
    return (doc as unknown as { lastAutoTable: { finalY: number } }).lastAutoTable.finalY + ESPACO_SECAO;
  }

  private addAssinaturas(doc: jsPDF, dados: TermoPdfDados, y: number): void {
    y = this.garantirEspaco(doc, y, 45);
    const larguraLinha = 75;
    const colunaEsquerda = MARGEM;
    const colunaDireita = MARGEM + LARGURA_TEXTO - larguraLinha;

    doc.setDrawColor(...COR_TEXTO);
    doc.setLineWidth(0.3);
    doc.line(colunaEsquerda, y, colunaEsquerda + larguraLinha, y);
    doc.line(colunaDireita, y, colunaDireita + larguraLinha, y);
    y += 5;

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(TAMANHO_FONTE_CORPO);
    doc.setTextColor(...COR_TEXTO);
    doc.text(`Nome: ${dados.colaborador.nome}`, colunaEsquerda, y);
    doc.text(`CPF: ${dados.colaborador.cpf}`, colunaEsquerda, y + 5);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...COR_MARCA);
    doc.text(LABEL_COLABORADOR, colunaEsquerda, y + 11);

    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...COR_TEXTO);
    doc.text(`Nome: ${dados.assinanteRaro.nome}`, colunaDireita, y);
    // Sem CPF (devolução), o rótulo sobe pra logo abaixo do nome — sem linha em branco.
    let yDireita = y;
    if (dados.assinanteRaro.cpf) {
      yDireita += 5;
      doc.text(`CPF: ${dados.assinanteRaro.cpf}`, colunaDireita, yDireita);
    }
    yDireita += 6;
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...COR_MARCA);
    doc.text(dados.tipoFluxo === 'envio' ? LABEL_TESTEMUNHA : LABEL_SEGURANCA_INFORMACAO, colunaDireita, yDireita);
    if (dados.tipoFluxo === 'devolucao') {
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...COR_TEXTO);
      doc.text('RARO RECRUTAMENTO EM TI LTDA', colunaDireita, yDireita + 5);
    }
  }

  private async anexarFotos(doc: jsPDF, gruposFotos: GrupoFotosTermo[]): Promise<void> {
    for (const grupo of gruposFotos) {
      for (let index = 0; index < grupo.urls.length; index++) {
        const url = grupo.urls[index];
        try {
          const dataUrl = await urlParaDataUrl(url);
          const dimensoes = await obterDimensoesImagem(dataUrl);
          doc.addPage();
          const pageWidth = doc.internal.pageSize.getWidth();
          const pageHeight = doc.internal.pageSize.getHeight();

          doc.setFont('helvetica', 'bold');
          doc.setFontSize(TAMANHO_FONTE_CORPO + 2);
          doc.setTextColor(...COR_MARCA);
          doc.text(`${grupo.legenda} — foto ${index + 1} de ${grupo.urls.length}`, MARGEM, MARGEM);

          const topoImagem = MARGEM + ALTURA_LEGENDA_FOTO;
          const larguraMax = pageWidth - MARGEM * 2;
          const alturaMax = pageHeight - topoImagem - MARGEM;
          const escala = Math.min(larguraMax / dimensoes.width, alturaMax / dimensoes.height);
          const largura = dimensoes.width * escala;
          const altura = dimensoes.height * escala;
          const x = (pageWidth - largura) / 2;
          const yImagem = topoImagem + (alturaMax - altura) / 2;
          doc.addImage(dataUrl, formatoImagem(dataUrl), x, yImagem, largura, altura);
        } catch {
          // Uma foto que falhou ao carregar (rede/CORS) não pode quebrar o Termo inteiro —
          // mesmo raciocínio da logo em addCabecalho. Sem essa página, mas com o resto do
          // documento gerado normalmente.
        }
      }
    }
  }
}

export const termoPdfService = new TermoPdfServiceClass();
